import { useMemo } from 'react';
import {
  buildQueryArraySorters,
  buildQueryFilters,
  useGetCoworkers,
} from '@sib/api';
import { DEFAULT_PAGE, MAXIMUM_PAGINATION } from '@sib/api/constants';
import {
  useClientContactsAutocomplete,
  useVendorContactsAutocomplete,
} from '@sib/utils';

export interface UseContactCategoriesOptions {
  organizationUuid?: string | null;
  debouncedSearchTerm?: string;
  useEmail?: boolean;
}

export const useContactCategories = ({
  organizationUuid,
  debouncedSearchTerm = '',
  useEmail = false,
}: UseContactCategoriesOptions) => {
  const { data: coworkersData, isLoading: isLoadingCoworkers } =
    useGetCoworkers({
      page: DEFAULT_PAGE,
      size: MAXIMUM_PAGINATION,
      ...(debouncedSearchTerm && {
        filter: buildQueryFilters([
          {
            field: 'name',
            value: debouncedSearchTerm,
            operator: 'contains',
          },
        ]),
      }),
      sort: buildQueryArraySorters([
        {
          field: 'name',
          order: 'asc',
        },
      ]),
    });

  const clientContactAutomCompleteProps = useClientContactsAutocomplete({
    organizationUuid,
    search: debouncedSearchTerm,
    useEmail,
  });

  const vendorContactAutomCompleteProps = useVendorContactsAutocomplete({
    organizationUuid,
    search: debouncedSearchTerm,
    useEmail,
  });

  const coworkerContactOptions = useMemo(
    () =>
      coworkersData?.items?.map(employee => ({
        label: employee.name,
        value: useEmail ? employee.email : employee.id,
        secondaryValue: employee.id,
        color: 'var(--color-secondary)',
        onEdit: null,
      })) || [],
    [coworkersData, useEmail]
  );

  const coworkerContactsTotalItems = coworkersData?.totalItems ?? 0;

  return {
    vendorContact: vendorContactAutomCompleteProps,
    clientContact: clientContactAutomCompleteProps,
    internalEmployees: {
      options: coworkerContactOptions,
      totalItems: coworkerContactsTotalItems,
      isLoading: isLoadingCoworkers,
    },
  };
};
